
export const currentSignInDataVersion: string = "v2"; //needed to invalidate local storage when we change between endpoints, or add data like ssUserId to response.

export const lessonPageUrlFragment: string = '/lesson/';

// If enabled, a button to create a 7-day trial account on the website is shown instead of the in-app sign-up form.
// If disabled, users who sign up via the in-app sign-up form are given a 14-day trial w/o entering a credit card.
export const isSevenDayTrialEnabled: boolean = true;

// appRoutePaths can have placeholders, appPagePaths cannot
export const appRoutePaths = {
  bootCampPage: "/bootCamp/:difficulty?",
  componentPlaygroundPage: `/playground`,
  courseLessonsPage: "/course-lessons/:courseId",
  coursesPage: "/courses",
  createAccountPage: '/onboarding/create-account',
  createTrialAccountPage: '/onboarding/create-trial-account',
  dashboardPage: "/dashboard",
  firstPageAfterSignIn: "/bootCamp",
  infoPage: "/onboarding/info",
  keyboardWizard: `/onboarding/keyboard-wizard`,
  lastPageUsed: "/last-page-used",
  lessonPage: `${lessonPageUrlFragment}:id?/:segmentId?/:mode?`,
  pitchDetectionSelected:
    "/onboarding/pitch-detection-selected/pitch-detection-selected",
  playlistPage: "/playlist/:playlistId",
  settingsPage: "/settings/:page?",
  onboarding_disclosureAndConsent: `/onboarding/disclosure-and-consent`,
  onboarding_setupFeedback_allowMicrophoneAccess: `/onboarding/setup-feedback-and-scoring/allow-microphone-access`,
  onboarding_setupFeedback_micAccessNeeded: `/onboarding/setup-feedback-and-scoring/mic-access-needed`,
  onboarding_setupFeedback_youAreAllSet: `/onboarding/setup-feedback-and-scoring/you-are-all-set`,
  onboarding_setupFeedback_setupFeedbackAndScoring: `/onboarding/setup-feedback-and-scoring`,
  setupKeyboardLaterPage: `/onboarding/keyboard-setup/setup-keyboard-later`,
  setupYourKeyboardPage: `/onboarding/keyboard-setup/setup-your-keyboard`,
  signInPage: "/sign-in",
  socialLinkEmail: "/sign-in/social/email",
  socialLinkCode: "/sign-in/social/code",
  songsRouterPage: "/songs/*",
  unlockedSongLessonsPage: "/unlocked-song-lessons/:sectionId",
};

export const appPagePaths = {
  bootCampPage: "/bootCamp",
  componentPlaygroundPage: `/playground`,
  coursesPage: "/courses",
  createAccountPage: '/onboarding/create-account',
  createTrialAccountPage: '/onboarding/create-trial-account',
  dashboardPage: "/dashboard",
  infoPage: '/onboarding/info',
  keyboardWizard: `/onboarding/keyboard-wizard`,
  pitchDetectionSelected: "/onboarding/pitch-detection-selected",
  settingsPage: "/settings",
  onboarding_setupFeedback_setupFeedbackAndScoring: `/onboarding/setup-feedback-and-scoring`,
  setupKeyboardLaterPage: `/onboarding/keyboard-setup/setup-keyboard-later`,
  setupYourKeyboardPage: `/onboarding/keyboard-setup/setup-your-keyboard`,
  signIn: "/sign-in",
  socialLinkEmail: "/sign-in/social/email",
  socialLinkCode: "/sign-in/social/code",
  songDetailsPage: "/songDetails",
  songsPage: "/songs",
};

export const settingsPagePaths = {
  about: "/settings/about",
  keyboardAndVolumes: "/settings/keyboard-and-volumes",
  myAccount: "/settings/my-account",
  privacy: "/settings/privacy",
};

export enum logRocketAppId {
  LocalDev = 'playground-sessions-internal/webapp-local-kxdly',
  ReleaseDev = 'playground-sessions-internal/webapp-dev',
  ReleaseProduction = 'oexekj/app-30',
}

const baseOnboardingImageSource = "https://d29h8r0xu3kanz.cloudfront.net/images/onboarding";

const baseLogosImageSource = "https://d29h8r0xu3kanz.cloudfront.net/images/logos";
export const logosImages = {
  pgsPurple: `${baseLogosImageSource}/logoglyph-pgs-purple.png`,
  pgsWhite: `${baseLogosImageSource}/logoglyph-pgs-white.png`,
};

export const onboardingImagesSource = {
  albumsPNG: `${baseOnboardingImageSource}/albums-2025-09.png`,
  albumsWEBP: `${baseOnboardingImageSource}/albums-2025-09.webp`,
  handsOnKeyboardPortrait: `${baseOnboardingImageSource}/hands-on-keyboard-portrait.png`,
  manAtKeyboardLandscape: `${baseOnboardingImageSource}/man-at-keyboard-landscape.png`,
  manAtKeyboardPortrait: `${baseOnboardingImageSource}/man-at-keyboard-portrait.png`,

  // Welcome view
  // Small images for phones
  playgroundPeopleSmWEBP2x: `${baseOnboardingImageSource}/playground-people-sm@2x.webp`,
  playgroundPeopleSmPNG2x: `${baseOnboardingImageSource}/playground-people-sm@2x.png`,
  // Large images for tablets and desktops
  playgroundPeopleLgWEBP2x: `${baseOnboardingImageSource}/playground-people-lg@2x.webp`,
  playgroundPeopleLgPNG2x: `${baseOnboardingImageSource}/playground-people-lg@2x.png`,

  outlineKeyboard: `${baseOnboardingImageSource}/outline-keyboard.svg`,
  scorePopup: `${baseOnboardingImageSource}/score-popup@3x.png`,
  womanAtKeyboardLandscape: `${baseOnboardingImageSource}/woman-at-keyboard-landscape.png`,
  womanAtKeyboardPortrait: `${baseOnboardingImageSource}/woman-at-keyboard-portrait.png`,

  // Connect your keyboard & Setup complete
  keyboardWithoutHighlights:      `${baseOnboardingImageSource}/Keyboard_without_highlights.png`,
  keyboardWithoutHighlightsWEBP:  `${baseOnboardingImageSource}/Keyboard_without_highlights.webp`,

  // Set keyboard size
  // Small images for portrait-orientation phones, which require some additional width
  keyboardRightPortionSmPNG2x: `${baseOnboardingImageSource}/Keyboard-Right_portion-sm@2x.png`,
  keyboardLeftPortionSmPNG2x: `${baseOnboardingImageSource}/Keyboard-Left_portion-sm@2x.png`,
  // Medium images
  keyboardRightPortion10keysPNG2x:  `${baseOnboardingImageSource}/Keyboard-Right_portion-10_keys-square@2x.png`,
  keyboardLeftPortion10keysPNG2x:   `${baseOnboardingImageSource}/Keyboard-Left_portion-10_keys-square@2x.png`,
  // Large images
  keyboardRightPortionLgPNG2x:  `${baseOnboardingImageSource}/Keyboard-Right_portion-10_keys-lg@2x.png`,
  keyboardLeftPortionLgPNG2x:   `${baseOnboardingImageSource}/Keyboard-Left_portion-10_keys-lg@2x.png`,
}

const baseKeyboardImageSource = "https://d29h8r0xu3kanz.cloudfront.net/images/birds-eye-keyboard/keys4";
export const keyboardImages = {
  a0ImageSource: `${baseKeyboardImageSource}/key-a0.png`,
  aImageSource: `${baseKeyboardImageSource}/key-a.png`,
  blackKeyImageSource: `${baseKeyboardImageSource}/key-sharp-flat.png`,
  c8ImageSource: `${baseKeyboardImageSource}/key-c8.png`,
  cAndFImageSource: `${baseKeyboardImageSource}/key-c-f.png`,
  dImageSource: `${baseKeyboardImageSource}/key-d.png`,
  eAndBImageSource: `${baseKeyboardImageSource}/key-e.png`,
  gImageSource: `${baseKeyboardImageSource}/key-g.png`,
};

const baseIconImageSource = "https://d29h8r0xu3kanz.cloudfront.net/images/icons";
export const iconImages = {
  accuracyCaretDown: `${baseIconImageSource}/icn-accuracy-score-down.svg`,
  accuracyCaretUp: `${baseIconImageSource}/icn-accuracy-score-up.svg`,
  bKey: `${baseIconImageSource}/icn-piano-key-b.svg`,
  cKey: `${baseIconImageSource}/icn-piano-key-c.svg`,
  closeButton: `${baseIconImageSource}/icn-close-v2.svg`,
  dKey: `${baseIconImageSource}/icn-piano-key-d.svg`,
  checkmark: `${baseIconImageSource}/icn-checkmark.svg`, // The fill color cannot be changed dynamically with this image. Consider using the font-icon for a checkbox instead.
  info: `${baseIconImageSource}/icn-info.svg`,
  levelUp: `${baseIconImageSource}/icn-level-up-award.svg`,
  starLost: `${baseIconImageSource}/icn-star-lost3.svg`,
  starLostForCard: `${baseIconImageSource}/icn-star-card-lost.svg`,
  starWon: `${baseIconImageSource}/icn-star-won3.svg`,
  videoLesson: `${baseIconImageSource}/icn-video-lesson.svg`,
  handsOnKeyboard: `${baseIconImageSource}/icn-hands-both-solid-with-keys.svg`,
};

const miscImageSource = "https://d29h8r0xu3kanz.cloudfront.net/images/misc";
export const miscImages = {
  backingTrackImage: `${miscImageSource}/backing_track_slider.png`,
  blackMicrophone: `${miscImageSource}/icn-microphone.png`,
  genericCourse: `${miscImageSource}/icn-generic-course-300-v2.png`,
  headphones: `${miscImageSource}/headphones.png`,
  playgroundSessionsLogo: `${miscImageSource}/ps-logo.png`,
  usbImage: `${miscImageSource}/usb_plug.png`,
  whiteMicrophone: `${miscImageSource}/icn-microphone-white.png`,
  handsOnKeyboardAVIFSm: `${miscImageSource}/hands-on-keyboard-portrait.avif`,
  handsOnKeyboardWebPSm: `${miscImageSource}/hands-on-keyboard-portrait.webp`,
  handsOnKeyboardPNGSm: `${miscImageSource}/hands-on-keyboard-portrait.png`,
  handsOnKeyboardAVIFLg: `${miscImageSource}/hands-on-keyboard-landscape.avif`,
  handsOnKeyboardWebPLg: `${miscImageSource}/hands-on-keyboard-landscape.webp`,
  handsOnKeyboardPNGLg: `${miscImageSource}/hands-on-keyboard-landscape.png`,
};

export const soundAssets = {
  baseUrl: `https://d29h8r0xu3kanz.cloudfront.net`,
  metronome: {
    relativePath: `/sounds/app/metronome/lowest-quality-2/`,
    sampleFileNames: {
      loudness4: `hihat_short_4.mp3`,
    },
  },
  countIn: {
    relativePath: `/sounds/app/count-in/lowest-quality-2/`,
    sampleFileNames: {
      one: `one.mp3`,
      two: `two.mp3`,
      three: `three.mp3`,
      four: `four.mp3`,
      set: `set.mp3`,
      go: `go.mp3`,
    },
  },
  piano: {
    legacy: {
      pianoSoundsRelativePath: `/sounds/piano/legacy-piano-2`,
      // pianoSoundsRelativePath: `/sounds/piano/legacy-piano-lower-quality`,
      // pianoSoundsRelativePath: `/sounds/piano/legacy-piano-98k`,
      // pianoSoundsRelativePath: `/sounds/piano/legacy-piano-average-112k`,
      sampleFileNames: {
        lowestQualityRelativePath: `/`,
        fileType: ".mp3",
        midiHash: {
          21: `a0`,
          22: `a#0`,
          23: `b0`,
          24: `c1`,
          25: `c#1`,
          26: `d1`,
          27: `d#1`,
          28: `e1`,
          29: `f1`,
          30: `f#1`,
          31: `g1`,
          32: `g#1`,
          33: `a1`,
          34: `a#1`,
          35: `b1`,
          36: `c2`,
          37: `c#2`,
          38: `d2`,
          39: `d#2`,
          40: `e2`,
          41: `f2`,
          42: `f#2`,
          43: `g2`,
          44: `g#2`,
          45: `a2`,
          46: `a#2`,
          47: `b2`,
          48: `c3`,
          49: `c#3`,
          50: `d3`,
          51: `d#3`,
          52: `e3`,
          53: `f3`,
          54: `f#3`,
          55: `g3`,
          56: `g#3`,
          57: `a3`,
          58: `a#3`,
          59: `b3`,
          60: `c4`,
          61: `c#4`,
          62: `d4`,
          63: `d#4`,
          64: `e4`,
          65: `f4`,
          66: `f#4`,
          67: `g4`,
          68: `g#4`,
          69: `a4`,
          70: `a#4`,
          71: `b4`,
          72: `c5`,
          73: `c#5`,
          74: `d5`,
          75: `d#5`,
          76: `e5`,
          77: `f5`,
          78: `f#5`,
          79: `g5`,
          80: `g#5`,
          81: `a5`,
          82: `a#5`,
          83: `b5`,
          84: `c6`,
          85: `c#6`,
          86: `d6`,
          87: `d#6`,
          88: `e6`,
          89: `f6`,
          90: `f#6`,
          91: `g6`,
          92: `g#6`,
          93: `a6`,
          94: `a#6`,
          95: `b6`,
          96: `c7`,
          97: `c#7`,
          98: `d7`,
          99: `d#7`,
          100: `e7`,
          101: `f7`,
          102: `f#7`,
          103: `g7`,
          104: `g#7`,
          105: `a7`,
          106: `a#7`,
          107: `b7`,
          108: `c8`,
        },
      },
    },
    alicia: {
      pianoSoundsRelativePath: `/sounds/piano/alicia-keys`,
      sampleFileNames: {
        lowestQualityRelativePath: `/48k-lowest-quality2/`,
        mediumQualityRelativePath: `/48k-medium-quality2/`,
        fileType: ".mp3",
        midiHash: {
          21: `a0`,
          22: `a#0`,
          23: `b0`,
          24: `c1`,
          25: `c#1`,
          26: `d1`,
          27: `d#1`,
          28: `e1`,
          29: `f1`,
          30: `f#1`,
          31: `g1`,
          32: `g#1`,
          33: `a1`,
          34: `a#1`,
          35: `b1`,
          36: `c2`,
          37: `c#2`,
          38: `d2`,
          39: `d#2`,
          40: `e2`,
          41: `f2`,
          42: `f#2`,
          43: `g2`,
          44: `g#2`,
          45: `a2`,
          46: `a#2`,
          47: `b2`,
          48: `c3`,
          49: `c#3`,
          50: `d3`,
          51: `d#3`,
          52: `e3`,
          53: `f3`,
          54: `f#3`,
          55: `g3`,
          56: `g#3`,
          57: `a3`,
          58: `a#3`,
          59: `b3`,
          60: `c4`,
          61: `c#4`,
          62: `d4`,
          63: `d#4`,
          64: `e4`,
          65: `f4`,
          66: `f#4`,
          67: `g4`,
          68: `g#4`,
          69: `a4`,
          70: `a#4`,
          71: `b4`,
          72: `c5`,
          73: `c#5`,
          74: `d5`,
          75: `d#5`,
          76: `e5`,
          77: `f5`,
          78: `f#5`,
          79: `g5`,
          80: `g#5`,
          81: `a5`,
          82: `a#5`,
          83: `b5`,
          84: `c6`,
          85: `c#6`,
          86: `d6`,
          87: `d#6`,
          88: `e6`,
          89: `f6`,
          90: `f#6`,
          91: `g6`,
          92: `g#6`,
          93: `a6`,
          94: `a#6`,
          95: `b6`,
          96: `c7`,
          97: `c#7`,
          98: `d7`,
          99: `d#7`,
          100: `e7`,
          101: `f7`,
          102: `f#7`,
          103: `g7`,
          104: `g#7`,
          105: `a7`,
          106: `a#7`,
          107: `b7`,
          108: `c8`,
        },
      },
    },
    iowa: {
      pianoSoundsRelativePath: `/sounds/piano/iowa`,
      sampleFileNames: {
        loud48000RelativePath: `/ff-48000/Piano.ff.`,
        lowestQuality48kRelativePath: `/edited-trimmed-48k-lowest-quality/Piano.ff.`,
        mediumQuality44kRelativePath: `/edited-trimmed-44k-medium-quality/Piano.ff.`,
        lowerQuality44kRelativePath: `/edited-trimmed-44k-lower-quality/Piano.ff.`,
        lowestQuality44kRelativePath: `/edited-trimmed-44k-lowest-quality/Piano.ff.`,
        lowestQualityMono44kRelativePath: `/edited-trimmed-44k-mono-lowest-quality/Piano.ff.`,
        fileType: `.mp3`,
        //https://www.inspiredacoustics.com/en/MIDI_note_numbers_and_center_frequencies
        midiHash: {
          21: `A0`,
          22: `Bb0`,
          23: `B0`,
          24: `C1`,
          25: `Db1`,
          26: `D1`,
          27: `Eb1`,
          28: `E1`,
          29: `F1`,
          30: `Gb1`,
          31: `G1`,
          32: `Ab1`,
          33: `A1`,
          34: `Bb1`,
          35: `B1`,
          36: `C2`,
          37: `Db2`,
          38: `D2`,
          39: `Eb2`,
          40: `E2`,
          41: `F2`,
          42: `Gb2`,
          43: `G2`,
          44: `Ab2`,
          45: `A2`,
          46: `Bb2`,
          47: `B2`,
          48: `C3`,
          49: `Db3`,
          50: `D3`,
          51: `Eb3`,
          52: `E3`,
          53: `F3`,
          54: `Gb3`,
          55: `G3`,
          56: `Ab3`,
          57: `A3`,
          58: `Bb3`,
          59: `B3`,
          60: `C4`,
          61: `Db4`,
          62: `D4`,
          63: `Eb4`,
          64: `E4`,
          65: `F4`,
          66: `Gb4`,
          67: `G4`,
          68: `Ab4`,
          69: `A4`,
          70: `Bb4`,
          71: `B4`,
          72: `C5`,
          73: `Db5`,
          74: `D5`,
          75: `Eb5`,
          76: `E5`,
          77: `F5`,
          78: `Gb5`,
          79: `G5`,
          80: `Ab5`,
          81: `A5`,
          82: `Bb5`,
          83: `B5`,
          84: `C6`,
          85: `Db6`,
          86: `D6`,
          87: `Eb6`,
          88: `E6`,
          89: `F6`,
          90: `Gb6`,
          91: `G6`,
          92: `Ab6`,
          93: `A6`,
          94: `Bb6`,
          95: `B6`,
          96: `C7`,
          97: `Db7`,
          98: `D7`,
          99: `Eb7`,
          100: `E7`,
          101: `F7`,
          102: `Gb7`,
          103: `G7`,
          104: `Ab7`,
          105: `A7`,
          106: `Bb7`,
          107: `B7`,
          108: `C8`,
        },
      },
    },
  },
  guitar: {
    orangeTree: {
      guitarSoundsRelativePath: `/sounds/guitar/orange-tree-acoustic-guitar`,
      sampleFileNames: {
        loud: {
          loudRelativePath: `/loud/44k-low-quality/`,
          fileType: `.mp3`,
          midiHash: {
            21: `a0`,
            22: `a#0`,
            23: `b0`,
            24: `c1`,
            25: `c#1`,
            26: `d1`,
            27: `d#1`,
            28: `e1`,
            29: `f1`,
            30: `f#1`,
            31: `g1`,
            32: `g#1`,
            33: `a1`,
            34: `a#1`,
            35: `b1`,
            36: `c2`,
            37: `c#2`,
            38: `d2`,
            39: `d#2`,
            40: `e2`,
            41: `f2`,
            42: `f#2`,
            43: `g2`,
            44: `g#2`,
            45: `a2`,
            46: `a#2`,
            47: `b2`,
            48: `c3`,
            49: `c#3`,
            50: `d3`,
            51: `d#3`,
            52: `e3`,
            53: `f3`,
            54: `f#3`,
            55: `g3`,
            56: `g#3`,
            57: `a3`,
            58: `a#3`,
            59: `b3`,
            60: `c4`,
            61: `c#4`,
            62: `d4`,
            63: `d#4`,
            64: `e4`,
            65: `f4`,
            66: `f#4`,
            67: `g4`,
            68: `g#4`,
            69: `a4`,
            70: `a#4`,
            71: `b4`,
            72: `c5`,
            73: `c#5`,
            74: `d5`,
            75: `d#5`,
            76: `e5`,
            77: `f5`,
            78: `f#5`,
            79: `g5`,
            80: `g#5`,
            81: `a5`,
            82: `a#5`,
            83: `b5`,
            84: `c6`,
            85: `c#6`,
            86: `d6`,
            87: `d#6`,
            88: `e6`,
            89: `f6`,
            90: `f#6`,
            91: `g6`,
            92: `g#6`,
            93: `a6`,
            94: `a#6`,
            95: `b6`,
            96: `c7`,
            97: `c#7`,
            98: `d7`,
            99: `d#7`,
            100: `e7`,
            101: `f7`,
            102: `f#7`,
            103: `g7`,
            104: `g#7`,
            105: `a7`,
            106: `a#7`,
            107: `b7`,
            108: `c8`,
          },
        },
      },
    },
  },
};

export const allPianoKeys = {
  21: { noteName: "a", midiNumber: 21, keyColor: "white", octave: 0 },
  22: { noteName: "a#", midiNumber: 22, keyColor: "black", octave: 0 },
  23: { noteName: "b", midiNumber: 23, keyColor: "white", octave: 0 },
  24: { noteName: "c", midiNumber: 24, keyColor: "white", octave: 1 },
  25: { noteName: "c#", midiNumber: 25, keyColor: "black", octave: 1 },
  26: { noteName: "d", midiNumber: 26, keyColor: "white", octave: 1 },
  27: { noteName: "d#", midiNumber: 27, keyColor: "black", octave: 1 },
  28: { noteName: "e", midiNumber: 28, keyColor: "white", octave: 1 },
  29: { noteName: "f", midiNumber: 29, keyColor: "white", octave: 1 },
  30: { noteName: "f#", midiNumber: 30, keyColor: "black", octave: 1 },
  31: { noteName: "g", midiNumber: 31, keyColor: "white", octave: 1 },
  32: { noteName: "g#", midiNumber: 32, keyColor: "black", octave: 1 },
  33: { noteName: "a", midiNumber: 33, keyColor: "white", octave: 1 },
  34: { noteName: "a#", midiNumber: 34, keyColor: "black", octave: 1 },
  35: { noteName: "b", midiNumber: 35, keyColor: "white", octave: 1 },
  36: { noteName: "c", midiNumber: 36, keyColor: "white", octave: 2 },
  37: { noteName: "c#", midiNumber: 37, keyColor: "black", octave: 2 },
  38: { noteName: "d", midiNumber: 38, keyColor: "white", octave: 2 },
  39: { noteName: "d#", midiNumber: 39, keyColor: "black", octave: 2 },
  40: { noteName: "e", midiNumber: 40, keyColor: "white", octave: 2 },
  41: { noteName: "f", midiNumber: 41, keyColor: "white", octave: 2 },
  42: { noteName: "f#", midiNumber: 42, keyColor: "black", octave: 2 },
  43: { noteName: "g", midiNumber: 43, keyColor: "white", octave: 2 },
  44: { noteName: "g#", midiNumber: 44, keyColor: "black", octave: 2 },
  45: { noteName: "a", midiNumber: 45, keyColor: "white", octave: 2 },
  46: { noteName: "a#", midiNumber: 46, keyColor: "black", octave: 2 },
  47: { noteName: "b", midiNumber: 47, keyColor: "white", octave: 2 },
  48: { noteName: "c", midiNumber: 48, keyColor: "white", octave: 3 },
  49: { noteName: "c#", midiNumber: 49, keyColor: "black", octave: 3 },
  50: { noteName: "d", midiNumber: 50, keyColor: "white", octave: 3 },
  51: { noteName: "d#", midiNumber: 51, keyColor: "black", octave: 3 },
  52: { noteName: "e", midiNumber: 52, keyColor: "white", octave: 3 },
  53: { noteName: "f", midiNumber: 53, keyColor: "white", octave: 3 },
  54: { noteName: "f#", midiNumber: 54, keyColor: "black", octave: 3 },
  55: { noteName: "g", midiNumber: 55, keyColor: "white", octave: 3 },
  56: { noteName: "g#", midiNumber: 56, keyColor: "black", octave: 3 },
  57: { noteName: "a", midiNumber: 57, keyColor: "white", octave: 3 },
  58: { noteName: "a#", midiNumber: 58, keyColor: "black", octave: 3 },
  59: { noteName: "b", midiNumber: 59, keyColor: "white", octave: 3 },
  60: { noteName: "c", midiNumber: 60, keyColor: "white", octave: 4 },
  61: { noteName: "c#", midiNumber: 61, keyColor: "black", octave: 4 },
  62: { noteName: "d", midiNumber: 62, keyColor: "white", octave: 4 },
  63: { noteName: "d#", midiNumber: 63, keyColor: "black", octave: 4 },
  64: { noteName: "e", midiNumber: 64, keyColor: "white", octave: 4 },
  65: { noteName: "f", midiNumber: 65, keyColor: "white", octave: 4 },
  66: { noteName: "f#", midiNumber: 66, keyColor: "black", octave: 4 },
  67: { noteName: "g", midiNumber: 67, keyColor: "white", octave: 4 },
  68: { noteName: "g#", midiNumber: 68, keyColor: "black", octave: 4 },
  69: { noteName: "a", midiNumber: 69, keyColor: "white", octave: 4 },
  70: { noteName: "a#", midiNumber: 70, keyColor: "black", octave: 4 },
  71: { noteName: "b", midiNumber: 71, keyColor: "white", octave: 4 },
  72: { noteName: "c", midiNumber: 72, keyColor: "white", octave: 5 },
  73: { noteName: "c#", midiNumber: 73, keyColor: "black", octave: 5 },
  74: { noteName: "d", midiNumber: 74, keyColor: "white", octave: 5 },
  75: { noteName: "d#", midiNumber: 75, keyColor: "black", octave: 5 },
  76: { noteName: "e", midiNumber: 76, keyColor: "white", octave: 5 },
  77: { noteName: "f", midiNumber: 77, keyColor: "white", octave: 5 },
  78: { noteName: "f#", midiNumber: 78, keyColor: "black", octave: 5 },
  79: { noteName: "g", midiNumber: 79, keyColor: "white", octave: 5 },
  80: { noteName: "g#", midiNumber: 80, keyColor: "black", octave: 5 },
  81: { noteName: "a", midiNumber: 81, keyColor: "white", octave: 5 },
  82: { noteName: "a#", midiNumber: 82, keyColor: "black", octave: 5 },
  83: { noteName: "b", midiNumber: 83, keyColor: "white", octave: 5 },
  84: { noteName: "c", midiNumber: 84, keyColor: "white", octave: 6 },
  85: { noteName: "c#", midiNumber: 85, keyColor: "black", octave: 6 },
  86: { noteName: "d", midiNumber: 86, keyColor: "white", octave: 6 },
  87: { noteName: "d#", midiNumber: 87, keyColor: "black", octave: 6 },
  88: { noteName: "e", midiNumber: 88, keyColor: "white", octave: 6 },
  89: { noteName: "f", midiNumber: 89, keyColor: "white", octave: 6 },
  90: { noteName: "f#", midiNumber: 90, keyColor: "black", octave: 6 },
  91: { noteName: "g", midiNumber: 91, keyColor: "white", octave: 6 },
  92: { noteName: "g#", midiNumber: 92, keyColor: "black", octave: 6 },
  93: { noteName: "a", midiNumber: 93, keyColor: "white", octave: 6 },
  94: { noteName: "a#", midiNumber: 94, keyColor: "black", octave: 6 },
  95: { noteName: "b", midiNumber: 95, keyColor: "white", octave: 6 },
  96: { noteName: "c", midiNumber: 96, keyColor: "white", octave: 7 },
  97: { noteName: "c#", midiNumber: 97, keyColor: "black", octave: 7 },
  98: { noteName: "d", midiNumber: 98, keyColor: "white", octave: 7 },
  99: { noteName: "d#", midiNumber: 99, keyColor: "black", octave: 7 },
  100: { noteName: "e", midiNumber: 100, keyColor: "white", octave: 7 },
  101: { noteName: "f", midiNumber: 101, keyColor: "white", octave: 7 },
  102: { noteName: "f#", midiNumber: 102, keyColor: "black", octave: 7 },
  103: { noteName: "g", midiNumber: 103, keyColor: "white", octave: 7 },
  104: { noteName: "g#", midiNumber: 104, keyColor: "black", octave: 7 },
  105: { noteName: "a", midiNumber: 105, keyColor: "white", octave: 7 },
  106: { noteName: "a#", midiNumber: 106, keyColor: "black", octave: 7 },
  107: { noteName: "b", midiNumber: 107, keyColor: "white", octave: 7 },
  108: { noteName: "c", midiNumber: 108, keyColor: "white", octave: 8 },
};

/**
 * The part of the URL after the article id becomes visible after navigating to the article.
 * However, it changes occasionally (e.g., when the article title is updated)
 * and it's inconsequential,
 * thus it's excluded here.
 */
export const keyboardHelpLinks = {
  android: 'https://support.playgroundsessions.com/hc/en-us/articles/27194849197716',
  ipadAndIPhone: 'https://support.playgroundsessions.com/hc/en-us/articles/4408353373204',
  computer: 'https://support.playgroundsessions.com/hc/en-us/articles/360001011866',
  unknown: 'https://support.playgroundsessions.com/hc/en-us/categories/360000078043', // Contains links for all devices.
}
